import { makeAutoObservable, runInAction } from "mobx";
import { RootStore, ServiceStore } from "@intrasync/stores";
import { ViewStoreErrorHandler } from "@intrasync/stores/utils";
import type {
  Account,
  Job,
  QuoteProduct,
  quotes,
  quotesData,
  quoteLineItems as QuoteLineItemType,
  quoteLineItemsData,
  CreateQuoteData,
} from "./types";

export default class QuoteGenerationViewStore {
  rootStore: RootStore;
  errorHandler: ViewStoreErrorHandler;

  // Observables - Step Management
  currentStep = 1;
  isLoading = false;
  error: string | null = null;

  // Observables - Entity IDs
  selectedAccountId: number | undefined = undefined;
  selectedProjectId: number | undefined = undefined;
  createdQuoteId: number | undefined = undefined;

  // Observables - Created Quote
  createdQuote: quotes | null = null;

  // Mock Storage for demo - Made public so SalesOrderConversion can access them
  mockQuotes: quotes[] = [];
  mockQuoteLineItems: QuoteLineItemType[] = [];
  private nextMockQuoteId = 1000;

  // Mock Data for Demo
  private mockAccounts: Account[] = [
    {
      accountId: 1,
      name: "Acme Construction Co.",
      accountType: "Customer",
      paymentTerms: "Net 30",
      accountCode: "ACME001",
      email: "contact@acmeconstruction.com",
      phone: "(555) 123-4567",
      website: "www.acmeconstruction.com",
      status: "Active",
      industry: "Construction",
      rating: "A+",
      annualRevenue: 5000000,
      numberOfEmployees: 50,
    },
    {
      accountId: 2,
      name: "BuildRight Industries",
      accountType: "Customer",
      paymentTerms: "Net 45",
      accountCode: "BUILD002",
      email: "info@buildright.com",
      phone: "(555) 234-5678",
      website: "www.buildright.com",
      status: "Active",
      industry: "Infrastructure",
      rating: "A",
      annualRevenue: 8000000,
      numberOfEmployees: 120,
    },
    {
      accountId: 3,
      name: "City Public Works",
      accountType: "Government",
      paymentTerms: "Net 60",
      accountCode: "CITY003",
      email: "procurement@cityworks.gov",
      phone: "(555) 345-6789",
      website: "www.cityworks.gov",
      status: "Active",
      industry: "Government",
      rating: "AAA",
      annualRevenue: 50000000,
      numberOfEmployees: 500,
    },
    {
      accountId: 4,
      name: "Rapid Development LLC",
      accountType: "Customer",
      paymentTerms: "Net 30",
      accountCode: "RAPID004",
      email: "projects@rapiddev.com",
      phone: "(555) 456-7890",
      website: "www.rapiddev.com",
      status: "Active",
      industry: "Real Estate",
      rating: "B+",
      annualRevenue: 3000000,
      numberOfEmployees: 25,
    },
  ];

  private mockJobs: Job[] = [
    {
      id: 101,
      jobNumber: 2024001,
      name: "Highway 101 Culvert Installation",
      description: "Installation of drainage culverts along Highway 101",
      accountId: 1,
      status: "Active",
      startDate: "2024-01-15",
      endDate: "2024-06-30",
      projectManager: 1,
    },
    {
      id: 102,
      jobNumber: 2024002,
      name: "Downtown Parking Structure",
      description: "Precast concrete barriers for new parking structure",
      accountId: 1,
      status: "Planning",
      startDate: "2024-02-01",
      endDate: "2024-08-31",
      projectManager: 2,
    },
    {
      id: 103,
      jobNumber: 2024003,
      name: "Industrial Park Infrastructure",
      description: "K-rails and barriers for industrial park development",
      accountId: 2,
      status: "Active",
      startDate: "2024-01-10",
      endDate: "2024-12-31",
      projectManager: 1,
    },
    {
      id: 104,
      jobNumber: 2024004,
      name: "Bridge Reconstruction Project",
      description: "Custom precast components for bridge reconstruction",
      accountId: 2,
      status: "Active",
      startDate: "2024-03-01",
      endDate: "2024-09-30",
      projectManager: 3,
    },
    {
      id: 105,
      jobNumber: 2024005,
      name: "Main Street Flood Control",
      description: "Storm drain system and culvert installation",
      accountId: 3,
      status: "Active",
      startDate: "2024-02-15",
      endDate: "2024-07-31",
      projectManager: 2,
    },
    {
      id: 106,
      jobNumber: 2024006,
      name: "City Center Safety Barriers",
      description: "Installation of safety barriers in city center",
      accountId: 3,
      status: "Planning",
      startDate: "2024-04-01",
      endDate: "2024-10-31",
      projectManager: 1,
    },
    {
      id: 107,
      jobNumber: 2024007,
      name: "Retail Complex Phase 1",
      description: "Precast panels and barriers for retail development",
      accountId: 4,
      status: "Active",
      startDate: "2024-01-20",
      endDate: "2024-05-31",
      projectManager: 3,
    },
  ];

  private mockProducts: QuoteProduct[] = [
    {
      id: "201",
      productCode: "CUL-24",
      productName: "24\" Round Culvert",
      formId: "FORM-001",
      departmentId: "DEPT-PRECAST",
      yardsPerUnit: 0.98,
      cubicMetersPerUnit: 0.75,
      standardCostPerYard: 280.00,
      inboundFreightPercent: 5,
      totalLaborCost: 120.00,
      totalOverheadCost: 80.00,
      estimatedCost: 450.00,
      // Quote-specific fields
      basePrice: 550.00,
      weight: 1200,
      dimensions: "24\" dia x 8'",
      material: "Reinforced Concrete",
      specifications: "ASTM C76, Class III",
      productType: "culvert",
      createdAt: "2024-01-01T00:00:00Z",
      updatedAt: "2024-01-01T00:00:00Z",
    },
    {
      id: "202",
      productCode: "CUL-36",
      productName: "36\" Round Culvert",
      formId: "FORM-002",
      departmentId: "DEPT-PRECAST",
      yardsPerUnit: 1.96,
      cubicMetersPerUnit: 1.50,
      standardCostPerYard: 320.00,
      inboundFreightPercent: 5,
      totalLaborCost: 180.00,
      totalOverheadCost: 120.00,
      estimatedCost: 750.00,
      // Quote-specific fields
      basePrice: 950.00,
      weight: 2400,
      dimensions: "36\" dia x 8'",
      material: "Reinforced Concrete",
      specifications: "ASTM C76, Class III",
      productType: "culvert",
      createdAt: "2024-01-01T00:00:00Z",
      updatedAt: "2024-01-01T00:00:00Z",
    },
    {
      id: "203",
      productCode: "KR-12",
      productName: "12' K-Rail Barrier",
      formId: "FORM-KR12",
      departmentId: "DEPT-BARRIER",
      yardsPerUnit: 1.48,
      cubicMetersPerUnit: 1.13,
      standardCostPerYard: 200.00,
      inboundFreightPercent: 8,
      totalLaborCost: 100.00,
      totalOverheadCost: 60.00,
      estimatedCost: 380.00,
      // Quote-specific fields
      basePrice: 480.00,
      weight: 3800,
      dimensions: "12' x 32\" x 24\"",
      material: "Reinforced Concrete",
      specifications: "NCHRP 350 TL-3",
      productType: "k-rail",
      createdAt: "2024-01-01T00:00:00Z",
      updatedAt: "2024-01-01T00:00:00Z",
    },
    {
      id: "204",
      productCode: "KR-20",
      productName: "20' K-Rail Barrier",
      formId: "FORM-KR20",
      departmentId: "DEPT-BARRIER",
      yardsPerUnit: 2.47,
      cubicMetersPerUnit: 1.89,
      standardCostPerYard: 200.00,
      inboundFreightPercent: 8,
      totalLaborCost: 160.00,
      totalOverheadCost: 100.00,
      estimatedCost: 620.00,
      // Quote-specific fields
      basePrice: 780.00,
      weight: 6300,
      dimensions: "20' x 32\" x 24\"",
      material: "Reinforced Concrete",
      specifications: "NCHRP 350 TL-3",
      productType: "k-rail",
      createdAt: "2024-01-01T00:00:00Z",
      updatedAt: "2024-01-01T00:00:00Z",
    },
    {
      id: "205",
      productCode: "BAR-SEC",
      productName: "Security Barrier",
      formId: "FORM-SEC",
      departmentId: "DEPT-BARRIER",
      yardsPerUnit: 1.76,
      cubicMetersPerUnit: 1.35,
      standardCostPerYard: 350.00,
      inboundFreightPercent: 10,
      totalLaborCost: 200.00,
      totalOverheadCost: 150.00,
      estimatedCost: 850.00,
      // Quote-specific fields
      basePrice: 1050.00,
      weight: 4500,
      dimensions: "10' x 42\" x 24\"",
      material: "Reinforced Concrete",
      specifications: "ASTM C825",
      productType: "barrier",
      createdAt: "2024-01-01T00:00:00Z",
      updatedAt: "2024-01-01T00:00:00Z",
    },
    {
      id: "206",
      productCode: "PRE-WALL-8",
      productName: "8' Precast Wall Panel",
      formId: "FORM-WALL8",
      departmentId: "DEPT-ARCH",
      yardsPerUnit: 1.25,
      cubicMetersPerUnit: 0.96,
      standardCostPerYard: 700.00,
      inboundFreightPercent: 12,
      totalLaborCost: 250.00,
      totalOverheadCost: 175.00,
      estimatedCost: 1200.00,
      // Quote-specific fields
      basePrice: 1500.00,
      weight: 3200,
      dimensions: "8' x 8' x 6\"",
      material: "Precast Concrete",
      specifications: "ACI 318",
      productType: "precast",
      createdAt: "2024-01-01T00:00:00Z",
      updatedAt: "2024-01-01T00:00:00Z",
    },
    {
      id: "207",
      productCode: "PRE-WALL-12",
      productName: "12' Precast Wall Panel",
      formId: "FORM-WALL12",
      departmentId: "DEPT-ARCH",
      yardsPerUnit: 1.88,
      cubicMetersPerUnit: 1.44,
      standardCostPerYard: 700.00,
      inboundFreightPercent: 12,
      totalLaborCost: 350.00,
      totalOverheadCost: 250.00,
      estimatedCost: 1800.00,
      // Quote-specific fields
      basePrice: 2200.00,
      weight: 4800,
      dimensions: "12' x 8' x 6\"",
      material: "Precast Concrete",
      specifications: "ACI 318",
      productType: "precast",
      createdAt: "2024-01-01T00:00:00Z",
      updatedAt: "2024-01-01T00:00:00Z",
    },
    {
      id: "208",
      productCode: "PIPE-48",
      productName: "48\" RCP Pipe",
      formId: "FORM-P48",
      departmentId: "DEPT-PIPE",
      yardsPerUnit: 1.41,
      cubicMetersPerUnit: 1.08,
      standardCostPerYard: 550.00,
      inboundFreightPercent: 6,
      totalLaborCost: 220.00,
      totalOverheadCost: 145.00,
      estimatedCost: 1100.00,
      // Quote-specific fields
      basePrice: 1400.00,
      weight: 3600,
      dimensions: "48\" dia x 8'",
      material: "Reinforced Concrete",
      specifications: "ASTM C76, Class IV",
      productType: "pipe",
      createdAt: "2024-01-01T00:00:00Z",
      updatedAt: "2024-01-01T00:00:00Z",
    },
    {
      id: "209",
      productCode: "CUSTOM-PANEL",
      productName: "Custom Architectural Panel",
      formId: "FORM-CUSTOM",
      departmentId: "DEPT-ARCH",
      yardsPerUnit: 0,
      cubicMetersPerUnit: 0,
      standardCostPerYard: 1000.00,
      inboundFreightPercent: 15,
      totalLaborCost: 800.00,
      totalOverheadCost: 600.00,
      estimatedCost: 2500.00,
      // Quote-specific fields
      basePrice: 3500.00,
      weight: 0,
      dimensions: "Custom",
      material: "Architectural Concrete",
      specifications: "Per drawings",
      productType: "custom",
      createdAt: "2024-01-01T00:00:00Z",
      updatedAt: "2024-01-01T00:00:00Z",
    },
    {
      id: "210",
      productCode: "BOX-6x4",
      productName: "6'x4' Box Culvert",
      formId: "FORM-BOX64",
      departmentId: "DEPT-PRECAST",
      yardsPerUnit: 2.96,
      cubicMetersPerUnit: 2.27,
      standardCostPerYard: 400.00,
      inboundFreightPercent: 7,
      totalLaborCost: 300.00,
      totalOverheadCost: 200.00,
      estimatedCost: 1500.00,
      // Quote-specific fields
      basePrice: 1900.00,
      weight: 7600,
      dimensions: "6' x 4' x 8'",
      material: "Reinforced Concrete",
      specifications: "ASTM C1433",
      productType: "culvert",
      createdAt: "2024-01-01T00:00:00Z",
      updatedAt: "2024-01-01T00:00:00Z",
    },
  ];

  constructor(rootStore: RootStore) {
    makeAutoObservable(this, { rootStore: false, errorHandler: false });
    this.rootStore = rootStore;

    // Initialize standardized error handling
    this.errorHandler = new ViewStoreErrorHandler(
      rootStore,
      "QuoteGenerationViewStore",
      {
        enableErrorNotifications: true,
        enableReactionBatching: true,
        debugMode: false,
      }
    );
  }

  // Computed Values - Access entities from ServiceStores or use mock data
  get accounts(): Account[] {
    const store = this.rootStore.customersStore as ServiceStore;
    const storeEntities = store?.entities?.map((entity: unknown) => entity as Account) || [];

    // Return mock data if store is empty (for demo purposes)
    if (storeEntities.length === 0) {
      console.log("Using mock accounts data for demo");
      return this.mockAccounts;
    }
    return storeEntities;
  }

  get projects(): Array<{
    id: number;
    projectName: string;
    accountId?: number;
  }> {
    const store = this.rootStore.jobStore as ServiceStore;
    const storeEntities = store?.entities?.map((entity: unknown) => {
      const job = entity as Job;
      return {
        id: job.id,
        projectName: job.name || "",
        accountId: job.accountId,
      };
    }) || [];

    // Return mock data if store is empty (for demo purposes)
    if (storeEntities.length === 0) {
      console.log("Using mock projects data for demo");
      return this.mockJobs.map(job => ({
        id: job.id,
        projectName: job.name || "",
        accountId: job.accountId,
      }));
    }
    return storeEntities;
  }

  get products(): QuoteProduct[] {
    const store = this.rootStore.manufacturingProductsStore as ServiceStore;
    const storeEntities = store?.entities?.map((entity: unknown) => entity as QuoteProduct) || [];

    // Return mock data if store is empty (for demo purposes)
    if (storeEntities.length === 0) {
      console.log("Using mock products data for demo");
      return this.mockProducts;
    }
    return storeEntities;
  }

  get quoteLineItems(): QuoteLineItemType[] {
    if (!this.createdQuoteId) return [];
    const store = this.rootStore.quoteLineItemsStore as ServiceStore;
    const storeItems = store?.entities
      ?.filter(
        (item: unknown) => {
          const lineItem = item as QuoteLineItemType;
          return lineItem.quoteId === this.createdQuoteId;
        }
      ) || [];

    // Return mock data if store is empty
    if (storeItems.length === 0) {
      return this.mockQuoteLineItems.filter(item => item.quoteId === this.createdQuoteId);
    }
    return storeItems;
  }

  // Get all quotes (for SalesOrderConversion to access)
  get allQuotes(): quotes[] {
    const store = this.rootStore.quotesStore as ServiceStore;
    const storeQuotes = store?.entities?.map((entity: unknown) => entity as quotes) || [];

    // Return mock data if store is empty (for demo purposes)
    if (storeQuotes.length === 0 && this.mockQuotes.length > 0) {
      console.log("Using mock quotes data for demo");
      return this.mockQuotes;
    }
    return storeQuotes;
  }

  get workflowProgress(): number {
    return ((this.currentStep - 1) / 2) * 100;
  }

  // Actions - UI State Management
  setCurrentStep = (step: number) => {
    this.currentStep = step;
  };

  setSelectedAccount = (accountId: number | undefined) => {
    this.selectedAccountId = accountId;
  };

  setSelectedProject = (projectId: number | undefined) => {
    this.selectedProjectId = projectId;
  };

  setError = (_operation: string, error: string) => {
    this.error = error;
  };

  clearError = () => {
    this.error = null;
  };

  reset = () => {
    this.currentStep = 1;
    this.selectedAccountId = undefined;
    this.selectedProjectId = undefined;
    this.createdQuoteId = undefined;
    this.createdQuote = null;
    this.error = null;
    this.isLoading = false;
  };

  // Async Actions - Quote Operations
  createQuote = async (data: CreateQuoteData) => {
    // Prevent duplicate creation if already in progress
    if (this.isLoading) {
      console.log("Quote creation already in progress, skipping duplicate call");
      return;
    }

    this.isLoading = true;
    this.error = null;

    try {
      // Generate quote number with better format
      const timestamp = Date.now();
      const quoteNumber = `QT-${new Date().getFullYear()}-${String(timestamp).slice(-6)}`;

      console.log(`Creating quote: ${quoteNumber} with ${data.lineItems.length} line items`);

      // Calculate subtotal from line items
      const subtotal = data.lineItems.reduce((sum, item) => sum + item.totalPrice, 0);

      // Create quote with required fields matching the API types
      const newQuote: quotesData = {
        quoteNumber,
        accountId: data.accountId,
        jobId: data.jobId,
        salesPersonId: data.salesPersonId,
        quoteDate: data.quoteDate,
        expirationDate: data.expirationDate,
        subtotalAmount: subtotal,
        totalAmount: data.totalAmount,
        currency: data.currency,
        status: data.status,
        revision: 1,
      };

      // Try to use ServiceStore first, fallback to mock for demo
      const quoteStore = this.rootStore.quotesStore as ServiceStore;
      let createdQuote: any;
      let createdLineItemsCount = 0;

      // Check if the store exists and can create quotes
      if (quoteStore?.create) {
        try {
          // Use the actual create method from ServiceStore
          createdQuote = await quoteStore.create(newQuote);

          if (createdQuote) {
            console.log(`Quote created successfully with ID: ${createdQuote.id}`);

            // Create line items using ServiceStore
            const lineItemStore = this.rootStore.quoteLineItemsStore as ServiceStore;

            for (const item of data.lineItems) {
              const lineItem: quoteLineItemsData = {
                quoteId: Number(createdQuote.id),
                lineNumber: item.lineNumber,
                itemType: item.itemType,
                panelId: item.panelId,
                productCode: item.productCode,
                quantity: item.quantity,
                unitPrice: item.unitPrice,
                discountPercent: item.discountPercent,
                totalPrice: item.totalPrice,
                description: item.description,
                extendedPrice: item.quantity * item.unitPrice,
              };

              // Create the line item using the service store
              try {
                await lineItemStore.create(lineItem);
                createdLineItemsCount++;
              } catch (lineError) {
                console.error("Failed to create line item:", lineError);
              }
            }
          }
        } catch (error) {
          console.log("ServiceStore not available, using mock data for demo");
          createdQuote = null; // Fall through to mock
        }
      }

      // If ServiceStore didn't work, use mock data for demo
      if (!createdQuote) {
        console.log("Using mock quote creation for demo");

        // Create mock quote
        const mockQuoteId = this.nextMockQuoteId++;
        createdQuote = {
          id: mockQuoteId,
          ...newQuote,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        } as quotes;

        // Store in mock storage
        this.mockQuotes.push(createdQuote);

        // Create mock line items
        for (const item of data.lineItems) {
          const mockLineItem: QuoteLineItemType = {
            id: this.nextMockQuoteId++,
            quoteId: mockQuoteId,
            lineNumber: item.lineNumber,
            itemType: item.itemType,
            panelId: item.panelId,
            productCode: item.productCode,
            quantity: item.quantity,
            unitPrice: item.unitPrice,
            discountPercent: item.discountPercent,
            totalPrice: item.totalPrice,
            description: item.description,
            extendedPrice: item.quantity * item.unitPrice,
          };
          this.mockQuoteLineItems.push(mockLineItem);
          createdLineItemsCount++;
        }

        // Simulate async delay
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      console.log(`Created ${createdLineItemsCount} line items for quote ${createdQuote.id}`);

      runInAction(() => {
        this.createdQuoteId = typeof createdQuote.id === 'string' ? parseInt(createdQuote.id, 10) : createdQuote.id;
        // Cast the ServiceStore response to our quotes type
        this.createdQuote = {
          ...createdQuote,
          id: typeof createdQuote.id === 'string' ? parseInt(createdQuote.id, 10) : createdQuote.id
        } as quotes;
        this.currentStep = 3; // Move to summary
      });

      this.rootStore.notificationStore?.addNotification({
        message: `Quote ${createdQuote.quoteNumber} created successfully`,
        options: { variant: "success" },
      });

    } catch (error) {
      runInAction(() => {
        this.error = (error as Error).message || "Failed to create quote";
      });

      this.rootStore.notificationStore?.addNotification({
        message: this.error,
        options: { variant: "error" },
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  // Disposal method for proper cleanup
  dispose = () => {
    if (this.errorHandler) {
      this.errorHandler.dispose();
    }
  };
}